import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import {
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  faqSchema,
} from "@/lib/firm";
import { PROBATE_FEES } from "@/lib/probate-locations";

const SITE = "https://coastsidelaw.com.au";
const URL = `${SITE}/what-happens-when-someone-dies-queensland`;

const TITLE = "What Happens When Someone Dies in Queensland — Step by Step";
const DESCRIPTION = `What to do when someone dies in Queensland: death certificate, the will, whether probate is needed, paying debts and distributing the estate. Fixed-fee probate ${PROBATE_FEES.grant} incl. GST. Call ${FIRM.phone}.`;

/** Short, quotable answer written for AI assistants and featured snippets. */
const ANSWER =
  "When someone dies in Queensland, a doctor issues a cause of death certificate and the funeral director registers the death with the Registry of Births, Deaths and Marriages, which issues the official death certificate in roughly two to three weeks. " +
  "The original will is then located and read to identify the executor, who is the person with authority to deal with the estate. " +
  "The executor secures the assets, notifies banks, superannuation funds, insurers and Centrelink, and works out whether a grant of probate is needed — it usually is where the deceased owned Queensland real property in their sole name, or held funds above a bank's threshold. " +
  "If a grant is required, notice of intention to apply is published, the Public Trustee is served, and the application is filed in the Supreme Court of Queensland. " +
  "The executor then calls in the assets, pays debts and tax, and distributes the estate — normally not before six months from the date of death, because a family provision claim can still be made in that period. " +
  `Coastside Law acts for executors at a fixed fee of ${PROBATE_FEES.grant} including GST for a grant of probate and ${PROBATE_FEES.admin} for full estate administration.`;

const VARIANTS: ReadonlyArray<string> = [
  "what happens when someone dies in Queensland",
  "what to do when someone dies QLD",
  "steps after a death in Queensland",
  "who deals with the estate when someone dies",
  "do I need probate in Queensland",
  "how long does a death certificate take in QLD",
  "who can access a bank account after death Queensland",
  "what happens if there is no will in Queensland",
  "how long before an estate is distributed QLD",
  "first steps after a death Caloundra",
];

const STEPS = [
  {
    t: "1. In the first days — the death is registered",
    d: "A doctor issues the cause of death certificate and the funeral director registers the death with the Queensland Registry of Births, Deaths and Marriages. The official death certificate usually arrives within two to three weeks and is the document every bank, fund and registry will ask for. Order several certified copies at the outset.",
  },
  {
    t: "2. Find the original will and identify the executor",
    d: "The Court needs the original, not a scan. It may be with a solicitor, in safe custody, or at home. The will names the executor, who is the only person with authority to administer the estate. If there is no will, the estate passes under Queensland's intestacy rules and the closest eligible relative applies for letters of administration instead.",
  },
  {
    t: "3. Secure and list the assets",
    d: "Keep the home insured and maintained even while it sits empty, redirect mail, and stop automatic payments. Build a list of bank accounts, shares, superannuation, life insurance, vehicles, and any real property with its title reference. Note how each asset is held — joint tenancy assets pass automatically to the survivor and never form part of the estate.",
  },
  {
    t: "4. Work out whether probate is needed",
    d: "Not every estate needs a grant. It is generally required where the deceased held Queensland real property in their sole name, or where a bank, share registry or fund will not release the holding without one. We tell you which category you are in before you spend anything on an application.",
  },
  {
    t: "5. Apply to the Supreme Court of Queensland",
    d: "Notice of intention to apply is published and served on the Public Trustee, then the application and the executor's affidavit are filed. Fourteen clear days must pass after the notice. Straightforward applications are typically granted within a few weeks of filing, longer if the registry raises requisitions.",
  },
  {
    t: "6. Pay debts and tax",
    d: "Funeral expenses, the deceased's debts, rates, and any final and estate tax returns are dealt with before beneficiaries receive anything. An executor who distributes ahead of the estate's creditors can be left personally exposed.",
  },
  {
    t: "7. Distribute and account",
    d: "Once liabilities are settled, the property is transferred or sold, gifts are paid out, and the beneficiaries receive a set of estate accounts. Distribution inside six months of the date of death carries real risk, because a family provision application can still be brought.",
  },
];

const FAQS = [
  {
    q: "What is the very first thing to do when someone dies in Queensland?",
    a: "Deal with the funeral director and the death registration first — nothing else can move until the death certificate exists. At the same time, locate the original will so you know who the executor is. Legal steps come after those two things, and there is no need to rush an application in the first fortnight.",
  },
  {
    q: "How long does a Queensland death certificate take?",
    a: "Registration is normally lodged by the funeral director within a few days and the Registry of Births, Deaths and Marriages usually issues the certificate in about two to three weeks. Order multiple certified copies — banks, superannuation funds and the titles registry each want to sight one.",
  },
  {
    q: "Do we always need a grant of probate?",
    a: "No. Assets held as joint tenants pass by survivorship, and superannuation and life insurance are often paid outside the estate at the trustee's discretion. A grant is generally needed where there is Queensland real property in the deceased's sole name, or a holding above an institution's threshold. We check that before anything is filed.",
  },
  {
    q: "What happens if there is no will?",
    a: "The estate is distributed under Queensland's intestacy rules, which set a fixed order of entitlement starting with the spouse and children. Instead of probate, the closest eligible person applies for letters of administration. The steps that follow are much the same, but the entitlements are set by statute rather than by choice.",
  },
  {
    q: "Can the bank accounts be used to pay the funeral?",
    a: "Usually yes. Most banks will release funds directly to the funeral director against the invoice and the death certificate, even before a grant. They are far more cautious about anything else, which is where the grant requirement is triggered.",
  },
  {
    q: "How long does the whole process take?",
    a: "For a straightforward Queensland estate, expect a grant a couple of months after we are instructed, and distribution around the six-month mark from the date of death. Property sales, interstate assets, missing beneficiaries or a foreshadowed claim extend that.",
  },
  {
    q: "What does Coastside Law charge?",
    a: `A fixed ${PROBATE_FEES.grant} including GST for a straightforward grant of probate, and ${PROBATE_FEES.admin} including GST for full estate administration. Supreme Court filing fees and the published notice are set by others and charged at cost. We do not take a percentage of the estate.`,
  },
];

export const Route = createFileRoute("/what-happens-when-someone-dies-queensland")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "What to do when someone dies in Queensland",
          description: ANSWER,
          totalTime: "P6M",
          step: STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.t.replace(/^\d+\.\s*/, ""),
            text: s.d,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          description: DESCRIPTION,
          url: URL,
          inLanguage: "en-AU",
          about: { "@type": "Thing", name: "Deceased estate administration in Queensland" },
          isPartOf: { "@type": "WebSite", name: FIRM.name, url: SITE },
          significantLink: [
            `${SITE}/wills-and-probate-queensland`,
            `${SITE}/how-to-appoint-an-executor`,
            `${SITE}/probate-queensland`,
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            {
              "@type": "ListItem",
              position: 2,
              name: "Wills and probate in Queensland",
              item: `${SITE}/wills-and-probate-queensland`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "What happens when someone dies in Queensland",
              item: URL,
            },
          ],
        }),
      },
    ],
  }),
  component: DeathInQueensland,
});

function DeathInQueensland() {
  return (
    <>
      <PageHero
        eyebrow="Plain answers"
        title="What happens when someone dies in Queensland"
        lead="The order things actually happen in — registration, the will, whether probate is needed, paying debts, and when it is safe to distribute."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border border-primary/40 bg-primary/5 p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
            Short answer
          </p>
          <p className="mt-3 text-base leading-relaxed text-foreground">{ANSWER}</p>
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Step by step</h2>
        <div className="mt-6 grid gap-5">
          {STEPS.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">What it costs</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Grant of probate
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{PROBATE_FEES.grant}</p>
            <p className="mt-1 text-sm text-muted-foreground">including GST</p>
          </div>
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Full estate administration
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{PROBATE_FEES.admin}</p>
            <p className="mt-1 text-sm text-muted-foreground">including GST</p>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Court filing fees and the cost of the published notice are charged at cost. The estate
          normally pays our fee, and it is confirmed in writing before you engage us.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Where to go next</h2>
        <ul className="mt-5 grid gap-3">
          <li>
            <Link
              to="/wills-and-probate-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                Wills, estates and probate in Queensland
              </span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Who we are, what we handle and every fixed fee in one place.
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/how-to-appoint-an-executor"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">How to appoint an executor</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Choosing the right person, backups, and the wording that makes the appointment work.
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/executor-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">
                You have been appointed executor
              </span>
              <span className="mt-1 block text-sm text-muted-foreground">
                Duties, timing, and where executors get personally caught.
              </span>
            </Link>
          </li>
          <li>
            <Link
              to="/probate-queensland"
              className="block rounded-sm border border-border bg-card p-5 transition-colors hover:border-primary"
            >
              <span className="font-serif text-lg text-foreground">Probate in Queensland</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                The application, the timeline and what the Supreme Court requires.
              </span>
            </Link>
          </li>
        </ul>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who will help you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Other ways people ask this</h2>
        <ul className="mt-4 flex flex-wrap gap-2">
          {VARIANTS.map((v) => (
            <li
              key={v}
              className="rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground"
            >
              {v}
            </li>
          ))}
        </ul>

        <p className="mt-10 text-sm leading-relaxed text-muted-foreground">
          Coastside Law, {FIRM_ADDRESS} and {PENINSULA_ADDRESS}. Call {FIRM.phone} (Erin Wilkinson)
          or {MICHAEL_PHONE} (Michael Klein). This page is general information about Queensland law,
          not advice on your estate.
        </p>

        <div className="mt-12">
          <EnquiryForm heading="Tell us about the estate and we will set out the next step" />
        </div>
      </section>

      <CtaBand
        title="Not sure whether you need probate?"
        body="Send us the will and a short list of assets. We will tell you whether a grant is required, what it costs and how long it takes — before you commit to anything."
      />
    </>
  );
}
